import { useRef, useState } from 'react';
import { CircleNotchIcon, EyeIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { cn } from '@/lib/utils';

/** A downloadable file: the short-lived link Download opens, and the name it saves under. */
export interface WorkbookFileLink {
  url: string;
  fileName: string;
}
export type WorkbookFileFormat = 'xlsx' | 'csv';

type Loaded =
  | { state: 'loading' }
  | { state: 'ready'; link: WorkbookFileLink }
  | { state: 'failed'; message: string };

/**
 * Previews a file Workbooks lets you download, in the spreadsheet viewer. It
 * loads the same link Download does, so the preview shows exactly the bytes a
 * download saves; the viewer's own Download saves that file. `formats` offers
 * each form the file downloads in (an Output File of one sheet is also CSV).
 */
export function WorkbookFilePreviewButton({
  title,
  load,
  formats = ['xlsx'],
  disabled = false,
  variant = 'outline',
}: {
  title: string;
  load: (format: WorkbookFileFormat) => Promise<WorkbookFileLink>;
  formats?: readonly WorkbookFileFormat[];
  disabled?: boolean;
  variant?: 'outline' | 'ghost';
}) {
  const gt = useGT();
  const [format, setFormat] = useState<WorkbookFileFormat | null>(null);
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  // Only the newest load shows: switching format supersedes one in flight.
  const latest = useRef(0);
  async function open(next: WorkbookFileFormat) {
    const request = ++latest.current;
    setFormat(next);
    setLoaded({ state: 'loading' });
    try {
      const link = await load(next);
      if (request === latest.current) setLoaded({ state: 'ready', link });
    } catch (error) {
      if (request !== latest.current) return;
      setLoaded({
        state: 'failed',
        message:
          error instanceof Error
            ? error.message
            : gt("This file couldn't be opened.", {
                context: 'workbook file preview error',
              }),
      });
    }
  }
  return (
    <>
      <Button
        size="sm"
        variant={variant}
        className="h-7"
        disabled={disabled}
        onClick={() => {
          const first = formats[0];
          if (first) void open(first);
        }}
        aria-label={gt('Preview {name}', {
          context: 'workbook file preview',
          name: title,
        })}
      >
        <EyeIcon />
        <T context="workbook file preview">Preview</T>
      </Button>
      <Dialog
        open={format !== null}
        onOpenChange={(isOpen) => !isOpen && setFormat(null)}
      >
        <DialogContent className="flex h-[85vh] flex-col gap-2 sm:max-w-6xl">
          <DialogHeader className="flex-row flex-wrap items-center gap-2 pr-8">
            <DialogTitle className="min-w-0 flex-1 truncate">
              {title}
            </DialogTitle>
            {formats.length > 1 && (
              <div
                role="group"
                aria-label={gt('File format', {
                  context: 'workbook file preview format',
                })}
                className="flex rounded-md border border-input p-0.5"
              >
                {formats.map((item) => (
                  <Button
                    key={item}
                    size="sm"
                    variant="ghost"
                    aria-pressed={item === format}
                    className={cn('h-6 px-2', item === format && 'bg-muted')}
                    onClick={() => item !== format && void open(item)}
                  >
                    {item === 'csv' ? (
                      <T context="workbook file preview format">CSV</T>
                    ) : (
                      <T context="workbook file preview format">xlsx</T>
                    )}
                  </Button>
                ))}
              </div>
            )}
          </DialogHeader>
          <div className="min-h-0 flex-1">
            {loaded.state === 'loading' ? (
              <p
                role="status"
                className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"
              >
                <CircleNotchIcon className="size-4 animate-spin" />
                <T context="workbook file preview loading">
                  Preparing the file…
                </T>
              </p>
            ) : loaded.state === 'failed' ? (
              <p role="alert" className="text-sm text-destructive">
                {loaded.message}
              </p>
            ) : (
              <XlsxViewer
                key={loaded.link.url}
                url={loaded.link.url}
                fileName={loaded.link.fileName}
                height="100%"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
